import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16">
      <Seo title="Terms of Service" description="Read the terms and conditions for using Bharat Estate." path="/terms" />
      <div className="container-luxury max-w-4xl">
        <div className="bg-white p-8 md:p-12 rounded-sm shadow-sm border border-border/50">
          <h1 className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-[#0F172A] mb-8">Terms of Service</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 2026</p>

          <div className="prose prose-lg max-w-none text-[#0F172A]">
            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">1. Agreement to Terms</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                By accessing or using Bharat Estate ("Platform"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Platform.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                These Terms apply to all visitors, users, and others who access or use the Platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">2. Definitions</h2>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li><strong>"Platform"</strong> refers to the Bharat Estate website and all associated services</li>
                <li><strong>"User"</strong> refers to any individual who accesses or uses the Platform</li>
                <li><strong>"Premium Member"</strong> refers to users who have purchased a premium membership subscription</li>
                <li><strong>"Listing"</strong> refers to property advertisements posted on the Platform</li>
                <li><strong>"Content"</strong> refers to all information, text, images, and materials uploaded to the Platform</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">3. User Accounts</h2>
              
              <h3 className="font-semibold text-lg mb-3">3.1 Account Creation</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                To use certain features of the Platform, you must register for an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate.
              </p>

              <h3 className="font-semibold text-lg mb-3">3.2 Account Security</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately of any unauthorized access to your account.
              </p>

              <h3 className="font-semibold text-lg mb-3">3.3 Account Termination</h3>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to suspend or terminate your account at our sole discretion, without notice, for conduct that violates these Terms or is harmful to other users, us, or third parties.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">4. Premium Membership</h2>
              
              <h3 className="font-semibold text-lg mb-3">4.1 Subscription</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Premium membership is required to list properties on the Platform. Premium memberships are available in monthly, quarterly, and yearly plans.
              </p>

              <h3 className="font-semibold text-lg mb-3">4.2 Activation</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Premium membership is activated immediately upon successful payment. The membership period begins from the date of activation.
              </p>

              <h3 className="font-semibold text-lg mb-3">4.3 Non-Refundable</h3>
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4">
                <p className="text-[#0F172A] font-medium">
                  IMPORTANT: Premium membership fees are NON-REFUNDABLE under any circumstances. By purchasing a premium membership, you acknowledge and agree that:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
                  <li>No refunds will be provided after payment is processed</li>
                  <li>No refunds for unused portion of the membership period</li>
                  <li>No refunds if your account is suspended or terminated for violation of Terms</li>
                  <li>No refunds for change of mind or dissatisfaction with services</li>
                </ul>
              </div>

              <h3 className="font-semibold text-lg mb-3">4.4 Auto-Renewal</h3>
              <p className="text-muted-foreground leading-relaxed">
                Memberships do not auto-renew. You must manually renew your subscription before expiry to continue listing properties.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">5. Property Listings</h2>
              
              <h3 className="font-semibold text-lg mb-3">5.1 Listing Requirements</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">Users posting listings must:</p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Have a valid premium membership</li>
                <li>Provide accurate and truthful information about the property</li>
                <li>Have legal authority to list the property</li>
                <li>Include clear and accurate images</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>

              <h3 className="font-semibold text-lg mb-3">5.2 Prohibited Listings</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">The following listings are prohibited:</p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Fraudulent or misleading property information</li>
                <li>Properties you do not own or have authority to list</li>
                <li>Illegal properties or properties involved in disputes</li>
                <li>Duplicate listings</li>
                <li>Properties with false pricing information</li>
              </ul>

              <h3 className="font-semibold text-lg mb-3">5.3 Listing Removal</h3>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to remove any listing that violates these Terms without notice or refund.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">6. User Conduct</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">You agree not to:</p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Use the Platform for any unlawful purpose</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Post false, misleading, or fraudulent content</li>
                <li>Attempt to gain unauthorized access to the Platform</li>
                <li>Use automated systems to access the Platform without permission</li>
                <li>Interfere with or disrupt the Platform's functionality</li>
                <li>Collect or store personal data of other users without consent</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">7. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                The Platform and its original content, features, and functionality are owned by Bharat Estate and are protected by international copyright, trademark, and other intellectual property laws.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                You retain ownership of content you post but grant us a license to use, display, and distribute such content on the Platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">8. Disclaimer of Warranties</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>The Platform will be uninterrupted or error-free</li>
                <li>Property listings are accurate or complete</li>
                <li>Any transaction between users will be successful</li>
                <li>The Platform will meet your specific requirements</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">9. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, BHARAT ESTATE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Loss of profits, revenue, or data</li>
                <li>Damages arising from property transactions</li>
                <li>Damages arising from user interactions</li>
                <li>Damages arising from unauthorized access to your account</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">10. Indemnification</h2>
              <p className="text-muted-foreground leading-relaxed">
                You agree to indemnify and hold harmless Bharat Estate and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Platform or violation of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">11. Governing Law</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts in Mumbai, Maharashtra.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">12. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the new Terms on this page. Your continued use of the Platform after changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">13. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                For questions about these Terms, please contact us:
              </p>
              <div className="bg-[#F9F9F7] p-6 rounded-sm">
                <p className="text-[#0F172A] font-medium">Bharat Estate</p>
                <p className="text-muted-foreground">Email: contact@bharat.estate</p>
                <p className="text-muted-foreground">Phone: +91 94650 24516</p>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-8 border-t flex gap-4">
            <Link to="/privacy" className="text-orange-500 hover:underline">Privacy Policy</Link>
            <Link to="/refund-policy" className="text-orange-500 hover:underline">Refund Policy</Link>
            <Link to="/grievance" className="text-orange-500 hover:underline">Grievance Redressal</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
